import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["Rings", "Necklaces & Chains", "Earrings", "Bangles & Bracelets", "Pendants", "Mangalsutra", "Anklets"];
const OCCASIONS = ["Daily Wear", "Festive", "Wedding", "Gifting"];
const PERS_TYPES = [
  { value: "engraving", label: "Name/initial engraving" },
  { value: "birthstone", label: "Birthstone selection" },
  { value: "date", label: "Date engraving" },
  { value: "message", label: "Message on the back" },
];

const EMPTY_VARIANT = {
  id: "", sku: "", options: {}, stock: 0, gross_weight: 0, net_weight: 0,
  making_mode: "pct", making_value: 0, flat_making: 0, wastage_pct: 0,
  stones: [], hallmark: "", certificate_no: "", price_override: 0,
};

const EMPTY = {
  title: "", slug: "", description: "", category: "Rings", metal: "gold", purity: "9K",
  occasion: "Daily Wear", made_to_order_days: 0, tags: [], images: [],
  status: "active", seo_title: "", seo_description: "", options: [],
  bis_applicable: false,
  variants: [{ ...EMPTY_VARIANT }], personalisation: { enabled: false, options: [] },
  variant_mode: "stored", design_number: "", weights: {}, weights_derived: [],
  colours: [], ring_size_base: "", size_min: 0, size_max: 0,
  image_sets: {}, video_sets: {}, spec_sheet: "",
  making_mode: "pct", making_value: 0, flat_making: 0, wastage_pct: 0,
  weight_tolerance_pct: 3, billing_weight_basis: "cad_plus_tolerance",
  manual_billing_weight: {}, fulfilment_map: {},
};

const WEIGHT_KEYS = ["925", "9K", "10K", "14K", "18K", "22K", "24K", "PT"];
const MEDIA_COLOURS = ["Yellow", "Rose", "White", "Silver"];

const EMPTY_STONE = { type: "", shape: "", count: 1, weight_carat: 0, rate_per_carat: 0, value: 0 };
const EMPTY_PERS = { id: "", type: "engraving", label: "", char_limit: 12, fonts: [], choices: [], surcharge: 0 };

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [tagsStr, setTagsStr] = useState("");
  const [stones, setStones] = useState([]); // shared across size variants, applied on save
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (id) {
      api.get(`/admin/products/${id}`).then((r) => {
        setForm({ ...EMPTY, ...r.data });
        setTagsStr((r.data.tags || []).join(", "));
        setStones(r.data.variants?.[0]?.stones || []);
      }).catch((e) => toast.error(errMsg(e)));
    }
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVariant = (i, k, v) =>
    set("variants", form.variants.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const numKeys = ["stock", "gross_weight", "net_weight", "making_value", "flat_making", "wastage_pct", "price_override"];

  const addOption = () => set("options", [...form.options, { name: "", values: [] }]);
  const setOption = (i, k, v) => set("options", form.options.map((o, j) => (j === i ? { ...o, [k]: v } : o)));

  const generateVariants = () => {
    const opts = form.options.filter((o) => o.name && o.values.length);
    let combos = [{}];
    opts.forEach((o) => {
      const next = [];
      combos.forEach((c) => o.values.forEach((v) => next.push({ ...c, [o.name]: v })));
      combos = next;
    });
    const existing = {};
    form.variants.forEach((v) => { existing[JSON.stringify(v.options)] = v; });
    const base = form.variants[0] || EMPTY_VARIANT;
    const variants = combos.map((c) => existing[JSON.stringify(c)] || { ...base, id: "", sku: "", options: c });
    set("variants", variants.length ? variants : [{ ...EMPTY_VARIANT }]);
  };

  const setPers = (k, v) => set("personalisation", { ...form.personalisation, [k]: v });
  const setPersOpt = (i, k, v) =>
    setPers("options", form.personalisation.options.map((o, j) => (j === i ? { ...o, [k]: v } : o)));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.variants.length) { toast.error("Add at least one variant"); return; }
    setSaving(true);
    const variants = form.variants.map((v) => ({ ...v, stones }));
    const personalisation = {
      ...form.personalisation,
      options: form.personalisation.options.map((o, i) => ({ ...o, id: o.id || `${o.type}-${i}` })),
    };
    const payload = {
      ...form, variants, personalisation,
      tags: tagsStr.split(",").map((t) => t.trim()).filter(Boolean),
      images: form.images.filter(Boolean),
      ...(form.variant_mode === "computed" ? { variants: [], stones } : {}),
    };
    try {
      if (id) await api.put(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Product saved");
      navigate("/admin/products");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="product-form">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">{id ? "Edit Product" : "New Product"}</h1>
        <Button onClick={save} disabled={saving} className="bg-stone-900 hover:bg-brand text-white btn-press" data-testid="save-product-btn">
          {saving ? "Saving…" : "Save Product"}
        </Button>
      </div>

      <section className="bg-white border border-neutral-200 p-6 space-y-4">
        <h2 className="font-heading font-semibold">Basics</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="pf-title" /></div>
          <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto if empty" data-testid="pf-slug" /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="pf-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Metal</Label>
            <Select value={form.metal} onValueChange={(v) => set("metal", v)}>
              <SelectTrigger data-testid="pf-metal"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="gold">Gold</SelectItem><SelectItem value="silver">Silver</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label>Purity</Label>
            <Select value={form.purity} onValueChange={(v) => set("purity", v)}>
              <SelectTrigger data-testid="pf-purity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(form.metal === "silver" ? ["925"] : ["9K", "14K", "18K", "22K", "24K"]).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Occasion</Label>
            <Select value={form.occasion} onValueChange={(v) => set("occasion", v)}>
              <SelectTrigger data-testid="pf-occasion"><SelectValue /></SelectTrigger>
              <SelectContent>{OCCASIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Made to order (days, 0 = ready)</Label><Input type="number" value={form.made_to_order_days} onChange={(e) => set("made_to_order_days", Number(e.target.value))} data-testid="pf-mto" /></div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="pf-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="draft">Draft</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label>Variant mode</Label>
            <Select value={form.variant_mode || "stored"} onValueChange={(v) => set("variant_mode", v)}>
              <SelectTrigger data-testid="pf-variant-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="stored">Stored variants</SelectItem>
                <SelectItem value="computed">Computed (CAD weight table)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.product_type === "coin" && (
            <label className="flex items-start gap-2 text-sm sm:col-span-2 pt-1" data-testid="pf-bis-wrap">
              <Switch checked={form.bis_applicable || false} onCheckedChange={(v) => set("bis_applicable", v)} data-testid="pf-bis-toggle" />
              <span>BIS hallmark applicable <span className="text-neutral-500">(gold coins only — keep off until the hallmarking centre confirms scope)</span></span>
            </label>
          )}
          <div className="sm:col-span-3"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="pf-description" /></div>
          <div><Label>Tags (comma)</Label><Input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} data-testid="pf-tags" /></div>
          <div><Label>SEO Title</Label><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} data-testid="pf-seo-title" /></div>
          <div><Label>SEO Description</Label><Input value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} data-testid="pf-seo-description" /></div>
        </div>
        <div>
          <Label>Image URLs (first = main, second = on-model shot)</Label>
          {form.images.map((img, i) => (
            <div key={i} className="flex gap-2 mt-2">
              <Input value={img} onChange={(e) => set("images", form.images.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`pf-image-${i}`} />
              <Button variant="ghost" size="icon" onClick={() => set("images", form.images.filter((_, j) => j !== i))}><Trash2 size={15} /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="mt-2" onClick={() => set("images", [...form.images, ""])} data-testid="pf-add-image"><Plus size={14} className="mr-1" /> Add Image</Button>
        </div>
      </section>

      {form.variant_mode === "computed" && (
        <section className="bg-white border border-neutral-200 p-6 space-y-5" data-testid="pf-computed-section">
          <h2 className="font-heading font-semibold">CAD Specification — computed variants</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><Label>Design number</Label><Input value={form.design_number || ""} onChange={(e) => set("design_number", e.target.value.toUpperCase())} data-testid="pf-design-number" /></div>
            <div><Label>Base ring size</Label><Input value={form.ring_size_base || ""} onChange={(e) => set("ring_size_base", e.target.value)} data-testid="pf-ring-size-base" /></div>
            <div><Label>Size min</Label><Input type="number" value={form.size_min || 0} onChange={(e) => set("size_min", Number(e.target.value))} data-testid="pf-size-min" /></div>
            <div><Label>Size max</Label><Input type="number" value={form.size_max || 0} onChange={(e) => set("size_max", Number(e.target.value))} data-testid="pf-size-max" /></div>
            <div>
              <Label>Making mode</Label>
              <Select value={form.making_mode || "pct"} onValueChange={(v) => set("making_mode", v)}>
                <SelectTrigger data-testid="pf-making-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pct">% of metal</SelectItem><SelectItem value="per_gram">₹/gram</SelectItem><SelectItem value="flat">Flat ₹</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Making value</Label><Input type="number" value={form.making_value || 0} onChange={(e) => set("making_value", Number(e.target.value))} data-testid="pf-making-value" /></div>
            <div><Label>Flat making ₹</Label><Input type="number" value={form.flat_making || 0} onChange={(e) => set("flat_making", Number(e.target.value))} data-testid="pf-flat-making" /></div>
            <div><Label>Wastage %</Label><Input type="number" value={form.wastage_pct || 0} onChange={(e) => set("wastage_pct", Number(e.target.value))} data-testid="pf-wastage" /></div>
            <div><Label>Weight tolerance %</Label><Input type="number" step="0.5" value={form.weight_tolerance_pct ?? 3} onChange={(e) => set("weight_tolerance_pct", Number(e.target.value))} data-testid="pf-tolerance" /></div>
            <div>
              <Label>Billing weight basis</Label>
              <Select value={form.billing_weight_basis || "cad_plus_tolerance"} onValueChange={(v) => set("billing_weight_basis", v)}>
                <SelectTrigger data-testid="pf-billing-basis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cad_plus_tolerance">CAD + tolerance</SelectItem>
                  <SelectItem value="cad">CAD exact</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Weight table (grams per purity)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {WEIGHT_KEYS.map((k) => {
                const derived = (form.weights_derived || []).includes(k);
                return (
                  <div key={k} className="flex items-center gap-2">
                    <span className="text-xs font-mono w-8">{k}</span>
                    <Input type="number" step="0.001" value={form.weights?.[k] ?? ""}
                      onChange={(e) => {
                        const w = { ...(form.weights || {}) };
                        if (e.target.value === "") delete w[k]; else w[k] = Number(e.target.value);
                        set("weights", w);
                        if (derived) set("weights_derived", (form.weights_derived || []).filter((x) => x !== k));
                      }}
                      className="h-8" data-testid={`pf-weight-${k.toLowerCase()}`} />
                    {derived && <span className="text-[10px] font-mono uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1 py-0.5" title="Derived from implied volume — not sheet-extracted">derived</span>}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-neutral-500 mt-1">9KT is derived from implied volume and marked “derived” — editing it converts it to a manual value.</p>
          </div>

          {form.billing_weight_basis === "manual" && (
            <div>
              <Label className="mb-2 block">Manual billing weight per purity (g)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.keys(form.weights || {}).map((k) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="text-xs font-mono w-8">{k}</span>
                    <Input type="number" step="0.001" value={form.manual_billing_weight?.[k] ?? ""}
                      onChange={(e) => { const m = { ...(form.manual_billing_weight || {}) }; if (e.target.value === "") delete m[k]; else m[k] = Number(e.target.value); set("manual_billing_weight", m); }}
                      className="h-8" data-testid={`pf-manual-bw-${k.toLowerCase()}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="mb-2 block">Fulfilment per purity (blank = default: 9KT/925 stock · 14KT/18KT/22KT made-to-order)</Label>
            <div className="flex flex-wrap gap-2" data-testid="pf-fulfilment-map">
              {Object.keys(form.weights || {}).map((k) => (
                <span key={k} className="flex items-center gap-1.5 text-xs border border-neutral-200 px-2 py-1">
                  <span className="font-mono">{k}</span>
                  <select value={form.fulfilment_map?.[k] || ""}
                    onChange={(e) => { const m = { ...(form.fulfilment_map || {}) }; if (e.target.value) m[k] = e.target.value; else delete m[k]; set("fulfilment_map", m); }}
                    className="h-6 border border-neutral-200 rounded text-[11px] bg-white" data-testid={`pf-fulfil-${k.toLowerCase()}`}>
                    <option value="">default</option>
                    <option value="in_stock">in stock</option>
                    <option value="made_to_order">made to order</option>
                  </select>
                </span>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Media completeness</Label>
            <div className="flex flex-wrap gap-2" data-testid="pf-media-completeness">
              {MEDIA_COLOURS.map((c) => {
                const n = (form.image_sets?.[c] || []).length;
                return <span key={c} className={`text-[11px] font-mono px-2 py-1 border ${n ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-neutral-200 text-neutral-400"}`} data-testid={`pf-media-${c.toLowerCase()}`}>{c}: {n ? `${n} img` : "none"}</span>;
              })}
              <span className={`text-[11px] font-mono px-2 py-1 border ${form.spec_sheet ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-neutral-200 text-neutral-400"}`} data-testid="pf-media-spec">Spec sheet: {form.spec_sheet ? "attached" : "none"}</span>
            </div>
            {form.spec_sheet && <a href={form.spec_sheet} target="_blank" rel="noreferrer" className="inline-block mt-2"><img src={form.spec_sheet} alt="CAD spec sheet" className="h-24 border" /></a>}
          </div>
        </section>
      )}

      {form.variant_mode !== "computed" && (
      <section className="bg-white border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold">Options & Variants (weights in grams)</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={addOption} data-testid="pf-add-option"><Plus size={14} className="mr-1" /> Add Option</Button>
            <Button variant="outline" size="sm" onClick={generateVariants} data-testid="pf-generate-variants">Generate Variants</Button>
          </div>
        </div>
        {form.options.map((opt, i) => (
          <div key={i} className="flex gap-2 mb-2 items-center">
            <Input placeholder="e.g. Size / Length" value={opt.name} onChange={(e) => setOption(i, "name", e.target.value)} className="w-40" data-testid={`pf-option-name-${i}`} />
            <Input placeholder="Values, comma separated (e.g. 8, 10, 12)" value={opt.values.join(", ")} onChange={(e) => setOption(i, "values", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid={`pf-option-values-${i}`} />
            <Button variant="ghost" size="icon" onClick={() => set("options", form.options.filter((_, j) => j !== i))}><Trash2 size={15} /></Button>
          </div>
        ))}

        <div className="overflow-x-auto mt-4">
          <table className="w-full text-xs min-w-[900px]">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
                {["Variant", "SKU", "Stock", "Gross g", "Net g", "Making mode", "Making", "Flat ₹", "Wastage %", "Hallmark/HUID", "Cert no.", "Override ₹", ""].map((h) => (
                  <th key={h} className="py-2 pr-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {form.variants.map((v, i) => (
                <tr key={i} className="border-b border-neutral-100" data-testid={`pf-variant-row-${i}`}>
                  <td className="py-1.5 pr-2 font-mono">{Object.entries(v.options || {}).map(([k, val]) => val).join("/") || "Std"}</td>
                  <td className="py-1.5 pr-2"><Input value={v.sku} onChange={(e) => setVariant(i, "sku", e.target.value)} className="h-8 w-28" data-testid={`pf-variant-sku-${i}`} /></td>
                  <td className="py-1.5 pr-2"><Input type="number" value={v.stock} onChange={(e) => setVariant(i, "stock", Number(e.target.value))} className="h-8 w-16" data-testid={`pf-variant-stock-${i}`} /></td>
                  <td className="py-1.5 pr-2"><Input type="number" step="0.1" value={v.gross_weight} onChange={(e) => setVariant(i, "gross_weight", Number(e.target.value))} className="h-8 w-20" data-testid={`pf-variant-gross-${i}`} /></td>
                  <td className="py-1.5 pr-2"><Input type="number" step="0.1" value={v.net_weight} onChange={(e) => setVariant(i, "net_weight", Number(e.target.value))} className="h-8 w-20" data-testid={`pf-variant-net-${i}`} /></td>
                  <td className="py-1.5 pr-2">
                    <Select value={v.making_mode} onValueChange={(val) => setVariant(i, "making_mode", val)}>
                      <SelectTrigger className="h-8 w-24" data-testid={`pf-variant-mmode-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="pct">% metal</SelectItem><SelectItem value="per_gram">₹/g</SelectItem></SelectContent>
                    </Select>
                  </td>
                  <td className="py-1.5 pr-2"><Input type="number" value={v.making_value} onChange={(e) => setVariant(i, "making_value", Number(e.target.value))} className="h-8 w-20" data-testid={`pf-variant-making-${i}`} /></td>
                  <td className="py-1.5 pr-2"><Input type="number" value={v.flat_making} onChange={(e) => setVariant(i, "flat_making", Number(e.target.value))} className="h-8 w-20" /></td>
                  <td className="py-1.5 pr-2"><Input type="number" value={v.wastage_pct} onChange={(e) => setVariant(i, "wastage_pct", Number(e.target.value))} className="h-8 w-16" data-testid={`pf-variant-wastage-${i}`} /></td>
                  <td className="py-1.5 pr-2"><Input value={v.hallmark} onChange={(e) => setVariant(i, "hallmark", e.target.value)} className="h-8 w-32" data-testid={`pf-variant-hallmark-${i}`} /></td>
                  <td className="py-1.5 pr-2"><Input value={v.certificate_no} onChange={(e) => setVariant(i, "certificate_no", e.target.value)} className="h-8 w-24" /></td>
                  <td className="py-1.5 pr-2"><Input type="number" value={v.price_override} onChange={(e) => setVariant(i, "price_override", Number(e.target.value))} className="h-8 w-24" data-testid={`pf-variant-override-${i}`} title=">0 locks a manual price (pre-GST subtotal), ignoring live rates" /></td>
                  <td><Button variant="ghost" size="icon" onClick={() => set("variants", form.variants.filter((_, j) => j !== i))} data-testid={`pf-variant-del-${i}`}><Trash2 size={14} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => set("variants", [...form.variants, { ...EMPTY_VARIANT }])} data-testid="pf-add-variant">
          <Plus size={14} className="mr-1" /> Add Variant
        </Button>
      </section>
      )}

      <section className="bg-white border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-heading font-semibold">Stones & Diamonds (shared across variants)</h2>
            <p className="text-xs text-neutral-500">Value = total carat weight × rate/carat (or enter a fixed value).</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setStones([...stones, { ...EMPTY_STONE }])} data-testid="pf-add-stone"><Plus size={14} className="mr-1" /> Add Stone</Button>
        </div>
        {stones.map((s, i) => (
          <div key={i} className="grid grid-cols-6 gap-2 mb-2 items-center" data-testid={`pf-stone-row-${i}`}>
            <Input placeholder="Type (Diamond)" value={s.type} onChange={(e) => setStones(stones.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))} data-testid={`pf-stone-type-${i}`} />
            <Input placeholder="Shape" value={s.shape} onChange={(e) => setStones(stones.map((x, j) => (j === i ? { ...x, shape: e.target.value } : x)))} />
            <Input type="number" placeholder="Count" value={s.count} onChange={(e) => setStones(stones.map((x, j) => (j === i ? { ...x, count: Number(e.target.value) } : x)))} />
            <Input type="number" step="0.01" placeholder="Carat (total)" value={s.weight_carat} onChange={(e) => setStones(stones.map((x, j) => (j === i ? { ...x, weight_carat: Number(e.target.value) } : x)))} data-testid={`pf-stone-carat-${i}`} />
            <Input type="number" placeholder="₹/carat (0=fixed)" value={s.rate_per_carat} onChange={(e) => setStones(stones.map((x, j) => (j === i ? { ...x, rate_per_carat: Number(e.target.value) } : x)))} data-testid={`pf-stone-rate-${i}`} />
            <div className="flex gap-1">
              <Input type="number" placeholder="Fixed ₹" value={s.value} onChange={(e) => setStones(stones.map((x, j) => (j === i ? { ...x, value: Number(e.target.value) } : x)))} />
              <Button variant="ghost" size="icon" onClick={() => setStones(stones.filter((_, j) => j !== i))}><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-white border border-neutral-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Switch checked={form.personalisation.enabled} onCheckedChange={(v) => setPers("enabled", v)} data-testid="pf-pers-enabled" />
          <h2 className="font-heading font-semibold">Personalisation options</h2>
          {form.personalisation.enabled && (
            <Button variant="outline" size="sm" className="ml-auto" onClick={() => setPers("options", [...form.personalisation.options, { ...EMPTY_PERS }])} data-testid="pf-pers-add">
              <Plus size={14} className="mr-1" /> Add Option
            </Button>
          )}
        </div>
        {form.personalisation.enabled && form.personalisation.options.map((o, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2 items-center border border-neutral-100 p-2" data-testid={`pf-pers-row-${i}`}>
            <Select value={o.type} onValueChange={(v) => setPersOpt(i, "type", v)}>
              <SelectTrigger data-testid={`pf-pers-type-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{PERS_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Label" value={o.label} onChange={(e) => setPersOpt(i, "label", e.target.value)} data-testid={`pf-pers-label-${i}`} />
            <Input type="number" placeholder="Char limit" value={o.char_limit} onChange={(e) => setPersOpt(i, "char_limit", Number(e.target.value))} />
            <Input type="number" placeholder="Surcharge ₹" value={o.surcharge} onChange={(e) => setPersOpt(i, "surcharge", Number(e.target.value))} data-testid={`pf-pers-surcharge-${i}`} />
            <div className="flex gap-1">
              {o.type === "engraving" && <Input placeholder="Fonts (Serif, Script)" value={(o.fonts || []).join(", ")} onChange={(e) => setPersOpt(i, "fonts", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} />}
              {o.type === "birthstone" && <Input placeholder="Choices (comma)" value={(o.choices || []).join(", ")} onChange={(e) => setPersOpt(i, "choices", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} />}
              <Button variant="ghost" size="icon" onClick={() => setPers("options", form.personalisation.options.filter((_, j) => j !== i))}><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
